import { cn } from "@/lib/utils";
import { Material } from "@/lib/sticker";

interface MaterialThumbnailProps {
  material: {
    id: Material;
    label: string;
    preview: string;
    description: string;
  };
  selected: boolean;
  onClick: () => void;
}

export const MaterialThumbnail = ({
  material,
  selected,
  onClick,
}: MaterialThumbnailProps) => (
  <div
    className={cn(
      "group relative cursor-pointer transition-all duration-200",
      "flex-1 min-w-0",
    )}
    onClick={onClick}
  >
    <div
      className={cn(
        "aspect-square rounded-md border transition-all duration-200 relative overflow-hidden p-2",
        material.preview,
        selected
          ? "border-primary ring-1 ring-primary/30 shadow-sm scale-105"
          : "border-border/60 hover:border-primary/50 hover:shadow-sm group-hover:scale-[1.02]",
      )}
    >
      {/* Material-specific effects */}
      {material.id === "glossy" && (
        <>
          <div className="absolute inset-2 bg-gradient-to-br from-white/60 to-transparent rounded-sm" />
          <div className="absolute top-1 left-1 w-2 h-2 bg-white/80 rounded-full blur-[1px]" />
        </>
      )}

      {material.id === "matte" && (
        <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/10" />
      )}

      {material.id === "translucent" && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
          <div className="absolute inset-0 backdrop-blur-[0.5px]" />
        </>
      )}

      {/* Selection overlay */}
      {selected && <div className="absolute inset-0 bg-primary/5 rounded-sm" />}

      {/* Selection indicator */}
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-primary rounded-full flex items-center justify-center z-20">
          <div className="w-1 h-1 bg-white rounded-full" />
        </div>
      )}

      {/* Material name inside thumbnail */}
      <div className="absolute bottom-1 left-1 right-1 z-10">
        <div className="text-[0.5rem] font-medium text-foreground/70 text-center leading-none">
          {material.label.toUpperCase()}
        </div>
      </div>
    </div>

    <div className="mt-1 text-center">
      <div className="text-[0.5rem] text-muted-foreground leading-none">
        {material.description}
      </div>
    </div>
  </div>
);
